var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import ReactHtmlParser from '@pcty/react-html-parser-fork';
import { typeOfComponent } from 'react-nanny';
import { escapeRegEx, isNilOrEmpty, isNotNilOrEmpty } from '@pcty/utils';
import { useHighlighter } from './HighlighterContext';
import { getConfig } from './private/utils';
import { Highlight } from './Highlighter.styled';
import { getAutomation } from '@citrus/runtime';
var Highlighter = function (_a) {
    var _b;
    var automationId = _a.automationId, caseSensitive = _a.caseSensitive, children = _a.children, className = _a.className, css = _a.css, shallow = _a.shallow, disabled = _a.disabled, find = _a.find, _c = _a.__TYPE, TYPE = _c === void 0 ? 'Highlighter' : _c, other = __rest(_a, ["automationId", "caseSensitive", "children", "className", "css", "shallow", "disabled", "find", "__TYPE"]);
    var context = (_b = useHighlighter()) !== null && _b !== void 0 ? _b : {};
    var config = getConfig(context, { automationId: automationId, caseSensitive: caseSensitive, children: children, className: className, css: css, shallow: shallow, disabled: disabled, find: find });
    if (config.disabled || disabled || (shallow && [undefined, 'string'].indexOf(typeOfComponent(children)) === -1))
        return React.createElement(React.Fragment, null, children);
    var _children = React.Children.toArray(children);
    var identifier = new Date().getTime().toString();
    if (shallow && typeOfComponent(children) === 'string') {
        return React.createElement(React.Fragment, null, highlight(children, config, identifier, other));
    }
    return (React.createElement(React.Fragment, null, _children.map(function (child) {
        if (shallow && typeof child !== 'string')
            return child;
        if (typeof child === 'string') {
            return highlight(child, config, identifier, other);
        }
        if (typeOfComponent(child.props.children) === 'string') {
            return React.cloneElement(child, {
                children: highlight(child.props.children, config, identifier, other),
            });
        }
        if (isNilOrEmpty(config.find))
            return child;
        return processChildNode(child, config, identifier, other);
    })));
};
/** Recursive function to iterate through child nodes and decide what to send for highlighting */
var processChildNode = function (children, config, identifier, other) {
    if (isNilOrEmpty(config.find))
        return children;
    if (typeof children === 'string')
        return highlight(children, config, identifier, other);
    return React.Children.toArray(children).map(function (child) {
        if (typeof child === 'string') {
            return highlight(child, config, identifier, other);
        }
        if (typeOfComponent(child.props.children) === 'string') {
            return React.cloneElement(child, {
                children: highlight(child.props.children, config, identifier, other),
            });
        }
        if (isNotNilOrEmpty(child.props.children)) {
            return React.cloneElement(child, {
                children: processChildNode(child.props.children, config, identifier, other),
            });
        }
        return child;
    });
};
/** Apply highlighting to string child */
var highlight = function (value, config, identifier, other) {
    var _a;
    if (isNilOrEmpty(config.find))
        return value;
    var regex = new RegExp(config.find, 'g' + (config.caseSensitive ? '' : 'i'));
    var matches = value.match(regex);
    if (isNilOrEmpty(matches))
        return value;
    value = value.replace(new RegExp("(".concat(Array.from(new Set(matches))
        .map(function (x) { return escapeRegEx(x); })
        .join('|'), ")"), 'g'), "<span class=\"".concat((_a = config.className) !== null && _a !== void 0 ? _a : identifier, "\">$1</span>"));
    return React.Children.toArray(ReactHtmlParser(value)).map(function (node, index) {
        var _a;
        return ((_a = node === null || node === void 0 ? void 0 : node.props) === null || _a === void 0 ? void 0 : _a.className) === identifier ? (React.createElement(Highlight, __assign({}, other, (config.automationId ? getAutomation(config.automationId) : {}), { className: config.className, css: config.css, key: node.props.children + index }), node.props.children)) : (node);
    });
};
Highlighter.defaultProps = {
    __TYPE: 'Highlighter',
};
export default Highlighter;
